import { useId, useMemo, useState, type FormEvent } from "react";
import { Search, Send } from "lucide-react";
import AIAssistField from "../AIAssistField";
import { Field } from "../FormLayout";
import { Modal } from "../Modal";
import type { CommunicationRecipient } from "../../services/communicationService";

export interface MessageDraft {
  recipient: number;
  subject: string;
  content: string;
  reply_to?: number;
}

/**
 * Writing one message to one person: who, about what, and what to say. As a
 * reply it opens addressed and titled, with the message answered shown above.
 */
export function MessageComposer({
  recipients,
  recipientsLoading,
  replyTo,
  pending,
  error,
  onSend,
  onClose,
}: {
  recipients: CommunicationRecipient[] | undefined;
  recipientsLoading?: boolean;
  /** Set when answering a message. */
  replyTo?: { id: number; recipient: { id: number; name: string }; subject: string; quote: string; from: string; sent: string };
  pending?: boolean;
  error?: string;
  onSend: (draft: MessageDraft) => void;
  onClose: () => void;
}) {
  const formId = useId();
  const [search, setSearch] = useState("");
  const [recipient, setRecipient] = useState(replyTo ? String(replyTo.recipient.id) : "");
  const [subject, setSubject] = useState(replyTo?.subject ?? "");
  const [content, setContent] = useState("");

  // Grouped by role and narrowed by the search box; the chosen person stays
  // listed even when the search no longer matches them.
  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const shown = (recipients ?? []).filter(
      (person) => String(person.id) === recipient || !term || `${person.name} ${person.role}`.toLowerCase().includes(term),
    );
    const byRole = new Map<string, CommunicationRecipient[]>();
    for (const person of shown) byRole.set(person.role, [...(byRole.get(person.role) ?? []), person]);
    return [...byRole.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [recipients, search, recipient]);
  const matches = groups.reduce((sum, [, people]) => sum + people.length, 0);

  const ready = !!recipient && subject.trim().length > 0 && content.trim().length > 0;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    onSend({ recipient: Number(recipient), subject: subject.trim(), content: content.trim(), reply_to: replyTo?.id });
  };

  return (
    <Modal
      isOpen
      size="2xl"
      title={replyTo ? `Reply to ${replyTo.recipient.name}` : "New message"}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          <button type="submit" form={formId} disabled={!ready || pending} className="btn btn-primary">
            <Send className="mr-2 h-4 w-4" />
            {pending ? "Sending…" : "Send message"}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="space-y-4">
        {error && <div role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {replyTo ? (
          <section className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {replyTo.from} wrote, {replyTo.sent}
            </p>
            <p className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-sm text-gray-700">{replyTo.quote}</p>
          </section>
        ) : (
          <Field as="div" label="To" hint={recipientsLoading ? "Loading the people you can write to…" : `${matches} of ${recipients?.length ?? 0} people shown.`}>
            <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_1fr]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="input pl-8"
                  placeholder="Find by name or role"
                  aria-label="Find a person"
                />
              </div>
              <select required value={recipient} onChange={(event) => setRecipient(event.target.value)} className="input" aria-label="Recipient">
                <option value="" disabled>Choose who to write to…</option>
                {groups.map(([role, people]) => (
                  <optgroup key={role} label={role}>
                    {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          </Field>
        )}

        <Field label="Subject">
          <input required maxLength={200} value={subject} onChange={(event) => setSubject(event.target.value)} className="input mt-1" placeholder="e.g. Term 3 fees for Joan Nakato" />
        </Field>

        <Field as="div" label="Message">
          <AIAssistField
            name="content"
            rows={7}
            value={content}
            onChange={(next) => setContent(next)}
            placeholder="Write your message"
            context={{ key: "communication.message", params: { recipient: recipient || null, subject, reply_to: replyTo?.id ?? null } }}
            draftLabel="Draft the message"
            draftHint="Reads who it is to, the subject and any message you are answering. Jot the points first, and it writes them up."
          />
        </Field>
      </form>
    </Modal>
  );
}
